import { useCallback, useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { m } from "#/paraglide/messages";
import { useMarket } from "#/contexts/MarketContext";
import { useDiamantReady } from "#/lib/api/diamant-ready";
import { DiamantReadySearchSchema } from "#/lib/schemas/diamant-ready-search";
import { DiamantReadySortBy } from "#/utils/diamant-ready-sort-by";
import { DiamantReadyPage } from "#/components/pages/diamant-ready/diamant-ready-page";
import type { DiamantReadyRow } from "#/components/organisms/diamant-ready/types";

const SORT_BY_STORAGE_KEY = "diamantReady.sortBy";

const SORT_BY_VALUES = new Set<string>(Object.values(DiamantReadySortBy));

const isDiamantReadySortBy = (value: string): value is DiamantReadySortBy =>
  SORT_BY_VALUES.has(value);

const readStoredSortBy = (): DiamantReadySortBy | undefined => {
  const raw = sessionStorage.getItem(SORT_BY_STORAGE_KEY);
  if (raw !== null && isDiamantReadySortBy(raw)) {
    return raw;
  }
  return undefined;
};

export const Route = createFileRoute("/$tenantCode/$market/diamant-ready")({
  beforeLoad: () => ({ getTitle: () => m["resources.diamantReady.name"]() }),
  validateSearch: DiamantReadySearchSchema,
  component: DiamantReadyRoute,
});

function DiamantReadyRoute() {
  const market = useMarket();
  const navigate = useNavigate();
  const { sortBy: urlSortBy } = Route.useSearch();

  const [initialSortBy] = useState<DiamantReadySortBy | undefined>(() =>
    readStoredSortBy(),
  );

  const sortBy: DiamantReadySortBy =
    urlSortBy ?? initialSortBy ?? DiamantReadySortBy.Status;

  useEffect(() => {
    if (urlSortBy !== undefined) {
      sessionStorage.setItem(SORT_BY_STORAGE_KEY, urlSortBy);
    }
  }, [urlSortBy]);

  const handleToggleSortBy = useCallback(
    (next: DiamantReadySortBy) => {
      void navigate({
        to: ".",
        search: (prev) => ({ ...prev, sortBy: next }),
        replace: true,
      });
    },
    [navigate],
  );

  const { data, isLoading, isFetching } = useDiamantReady({ market, sortBy });

  const rows = useMemo<DiamantReadyRow[]>(() => data?.member ?? [], [data]);

  return (
    <DiamantReadyPage
      data={rows}
      isLoading={isLoading}
      isFetching={isFetching}
      sortBy={sortBy}
      onToggleSortBy={handleToggleSortBy}
    />
  );
}
